import {
  BlockStack,
  Box,
  Button,
  Card,
  InlineStack,
  Layout,
  Link,
  List,
  Page,
  Text,
} from "@shopify/polaris";
import { type ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { t } from "../lib/i18n";

const SUPPORT_EMAIL = "support@ureyukibox.app";

interface FaqCardProps {
  question: string;
  children: ReactNode;
}

/** 1件の Q&A を見出し付きカードで表示する。 */
function FaqCard({ question, children }: FaqCardProps) {
  return (
    <Layout.Section>
      <Card>
        <BlockStack gap="300">
          <Text as="h3" variant="headingSm">
            {t(question)}
          </Text>
          {children}
        </BlockStack>
      </Card>
    </Layout.Section>
  );
}

export function Help() {
  const navigate = useNavigate();

  return (
    <Page title={t("よくある質問")}>
      <Layout>
        <Layout.Section>
          <Card>
            <BlockStack gap="200">
              <Text as="h2" variant="headingMd">
                {t("困ったときは")}
              </Text>
              <Text as="p" variant="bodyMd" tone="subdued">
                {t("UreyukiBox の使い方やトラブルの対処をまとめています。解決しない場合は")}{" "}
                <Link url={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link>{" "}
                {t("までご連絡ください。")}
              </Text>
              <InlineStack>
                <Button onClick={() => navigate("/contact")} variant="primary">
                  {t("お問い合わせを開く")}
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        <FaqCard question={t("インストールしたのに、エラーが出て画面が表示されません")}>
          <BlockStack gap="300">
            <Text as="p" variant="bodyMd">
              {t("多くの場合、次の操作で自動的に復旧します。上から順にお試しください。")}
            </Text>
            <List type="number">
              <List.Item>
                <Text as="span" variant="bodyMd" fontWeight="semibold">
                  {t("ページを再読み込みする（またはアプリを開き直す）。")}
                </Text>{" "}
                {t("UreyukiBox は Shopify との連携（認証）が未完了のとき、開き直すと自動的に認証をやり直します。多くの表示エラーはこれで解消します。")}
              </List.Item>
              <List.Item>
                <Text as="span" variant="bodyMd" fontWeight="semibold">
                  {t("少し待ってから「再試行」を押す。")}
                </Text>{" "}
                {t("一時的な通信エラーやサーバー側の混雑で、まれに読み込みに失敗することがあります。エラー画面の「再試行」ボタンを押すか、少し待ってからもう一度開いてください。")}
              </List.Item>
              <List.Item>
                <Text as="span" variant="bodyMd" fontWeight="semibold">
                  {t("一度アンインストールして入れ直した場合も、アプリを開くだけで大丈夫です。")}
                </Text>{" "}
                {t("再インストール後は、アプリを開くと自動的に Shopify と再連携します。特別な操作は要りません。")}
              </List.Item>
            </List>
            <Text as="p" variant="bodyMd">
              {t("それでも表示されないときは、以下をお試しください。")}
            </Text>
            <List type="bullet">
              <List.Item>
                {t("ブラウザのキャッシュを消す、またはシークレットウィンドウ／別のブラウザで開く。")}
              </List.Item>
              <List.Item>
                {t("Shopify 管理画面 →「設定」→「アプリと販売チャネル」から UreyukiBox を一度削除し、あらためてインストールする。")}
              </List.Item>
              <List.Item>
                {t("上記でも直らない場合は")}{" "}
                <Link url={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link>{" "}
                {t("へ、ストアの URL（◯◯.myshopify.com）と表示されたエラー内容を添えてご連絡ください。")}
              </List.Item>
            </List>
          </BlockStack>
        </FaqCard>

        <FaqCard question={t("データはいつ反映されますか（同期のタイミング）")}>
          <BlockStack gap="200">
            <Text as="p" variant="bodyMd">
              {t("インストール後、商品・在庫・注文を自動で読み込みます。初回はデータ量に応じて数分かかることがあります。以降は毎日自動で同期され、最新の状態に保たれます。")}
            </Text>
          </BlockStack>
        </FaqCard>

        <FaqCard question={t("在庫や注文が勝手に書き換えられませんか（安全性）")}>
          <BlockStack gap="200">
            <Text as="p" variant="bodyMd">
              {t("Shopify との同期は読み取りのみです。UreyukiBox がひとりでに在庫を書き換えることはありません。")}
            </Text>
            <Text as="p" variant="bodyMd">
              {t("在庫が変わるのは、あなたが操作した次の4つのときだけです：①入荷登録　②在庫調整　③棚卸しの確定　④拠点間移動。注文書は確認・出力のための下書きで、作成しただけでは在庫は動きません。")}
            </Text>
          </BlockStack>
        </FaqCard>

        {/* 🔴 スキャン欄は3画面にしかなく、その3画面に辿り着くまで「バーコードが
            使える」ことが画面のどこにも書かれていなかった＝機能があるのに見つからない。
            使える場所と要る道具を1箇所にまとめ、行き先ボタンまで添える。 */}
        <FaqCard question={t("バーコードリーダーは使えますか（どの画面で使えますか）")}>
          <BlockStack gap="300">
            <Text as="p" variant="bodyMd">
              {t("使えます。USB／Bluetooth のバーコードリーダーは「コードを打ち込んで Enter」を送るキーボードとして動くため、専用の設定やアプリは要りません。読み取り欄のある画面を開き、欄にカーソルがある状態で読み取ってください。1件ごとに欄は自動で空になり、次の読み取りを受け付けます。")}
            </Text>
            <List type="bullet">
              <List.Item>
                {t("入荷（発注済み・入荷）＝届いた商品を読み取って入荷数を積み上げる")}
              </List.Item>
              <List.Item>{t("棚卸し＝カウント中の実数を読み取りで入力する")}</List.Item>
              <List.Item>{t("在庫調整＝対象の商品を読み取りで選ぶ")}</List.Item>
            </List>
            <Text as="p" variant="bodyMd" tone="subdued">
              {t("読み取りは Shopify の商品に登録されたバーコード（JAN）と SKU の両方で照合します。どちらも未設定の商品は読み取れないため、その場合は手入力してください。棚に貼る値札は「発注点設定」画面の「バーコードラベル（印刷）」から出せます。")}
            </Text>
            <Text as="p" variant="bodyMd" tone="subdued">
              {t("スマートフォンのカメラでの読み取りには対応していません。")}
            </Text>
            <InlineStack gap="200">
              <Button onClick={() => navigate("/placed-orders")}>{t("入荷を開く")}</Button>
              <Button onClick={() => navigate("/stocktakes")}>{t("棚卸しを開く")}</Button>
              <Button onClick={() => navigate("/inventory-adjustments")}>
                {t("在庫調整を開く")}
              </Button>
            </InlineStack>
          </BlockStack>
        </FaqCard>

        <FaqCard question={t("「Shopify注文書用CSV」を取り込もうとしたらエラーになります")}>
          <BlockStack gap="300">
            <Text as="p" variant="bodyMd">
              {t("「Shopify注文書用CSV」は Shopify 純正の「注文書」画面専用の取り込みファイルです。商品管理トップの「インポート」（商品一括登録用）や「在庫」画面の「インポート」（在庫数一括更新用）は別のテンプレートのため、同じファイルを入れると「商品名の列は必須です」「無効なCSVヘッダーです」等のエラーになります。")}
            </Text>
            <Text as="p" variant="bodyMd">
              {t("正しい取り込み場所は、Shopify 管理画面の「商品管理」→「注文書」→「注文書を作成」（または既存の注文書を開く）を選び、その画面の中にある「インポート」からファイルを追加する手順です。")}
            </Text>
            <Text as="p" variant="bodyMd" tone="subdued">
              {t("純正側は商品を SKU またはバーコード（JAN）で照合するため、どちらも未設定の商品はこの CSV に含まれません。必要であれば純正の注文書画面で手動追加してください。")}
            </Text>
            <Text as="p" variant="bodyMd" tone="subdued">
              {t("この CSV は、Shopify 純正の注文書画面でも入荷予定・受領を管理したい場合に使います。UreyukiBox の中だけで回す場合は不要です（注文書の「注文済みとしてマーク」→「発注済み・入荷」ページで入荷を登録）。同じ発注を両方に登録すると二重に差し引かれるため、どちらか一方で管理してください。")}
            </Text>
            <Box>
              <Button onClick={() => navigate("/purchase-orders")}>
                {t("注文書を作成")}
              </Button>
            </Box>
          </BlockStack>
        </FaqCard>

        <FaqCard question={t("料金・プランについて教えてください")}>
          <BlockStack gap="300">
            <Text as="p" variant="bodyMd">
              {t("無料の Free プランから利用できます。標準の無料トライアルは1ストア通算14日です。残り日数とプランごとの機能はプラン画面でご確認いただけます。")}
            </Text>
            <Box>
              <Button onClick={() => navigate("/plans")}>
                {t("プランを見る")}
              </Button>
            </Box>
          </BlockStack>
        </FaqCard>

        <FaqCard question={t("Stocky からデータを取り込めますか")}>
          <BlockStack gap="300">
            <Text as="p" variant="bodyMd">
              {t("Stockyの公式エクスポートや、終了前に保存した対応CSV・画面表があれば、取得できた範囲をUreyukiBoxへ取り込めます。ファイルにない項目は推測して補完しません。")}
            </Text>
            <Box>
              <Button onClick={() => navigate("/stocky-csv")}>
                {t("データ取込を開く")}
              </Button>
            </Box>
          </BlockStack>
        </FaqCard>

        <FaqCard question={t("アンインストールするとデータはどうなりますか")}>
          <BlockStack gap="200">
            <Text as="p" variant="bodyMd">
              {t("アプリをアンインストールすると、当社が保持しているストアのデータは自動的に削除されます。再度インストールした場合は、Shopify から改めてデータを読み込み直します。")}
            </Text>
          </BlockStack>
        </FaqCard>

        <FaqCard question={t("欲しい機能があります（要望を送りたい）")}>
          <BlockStack gap="200">
            <Text as="p" variant="bodyMd">
              {t("ぜひお聞かせください。「こんな機能が欲しい」「ここが使いにくい」といったご要望は")}
              <Link url={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link>{" "}
              {t("までお気軽にお送りください。今後のアップデートの参考にさせていただきます。")}
            </Text>
          </BlockStack>
        </FaqCard>

        <Layout.Section>
          <Card>
            <BlockStack gap="200">
              <Text as="h3" variant="headingSm">
                {t("解決しないときは")}
              </Text>
              <Text as="p" variant="bodyMd">
                <Link url={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link>{" "}
                {t("までメールでお問い合わせください。ストアの URL と、できれば表示されたエラー内容を添えていただくと、確認がスムーズです。")}
              </Text>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
